import React, { createContext, ReactElement, useCallback, useState } from "react";

/** Mirrors INotificationSeverity from shapeshift-identity for app2-only notification context. */
export enum App2NotificationSeverity {
	success = 0,
	neutral = 1,
	warning = 2,
	error = 3,
	processInProgress = 4,
}

export interface App2Notification {
	title: string;
	body: string;
	severity: App2NotificationSeverity;
	lifeTime: number | null;
	timeAdded: Date;
	multiple?: boolean;
	id?: number;
}

export type App2NotifyProps = (
	body: string | null,
	title?: string,
	severity?: App2NotificationSeverity,
	lifeTime?: number | null,
	multiple?: boolean
) => void;

export interface App2NotificationContextValue {
	notification?: App2Notification;
	notify: App2NotifyProps;
}

const handleInitError = (): void => {
	throw new Error(
		"App2 Notification Context not initialized. Ensure the component is rendered inside App2NotificationProvider."
	);
};

export const App2NotificationContext = createContext<App2NotificationContextValue>({
	notify: handleInitError,
});

export interface App2NotificationProviderInnerProps {
	children: ReactElement | React.ReactNode;
}

/**
 * Inner provider that only supplies notification state and notify.
 * Same pattern as shapeshift-identity notificationContext.tsx; used by App2NotificationProvider.
 */
export const App2NotificationProviderInner: React.FC<App2NotificationProviderInnerProps> = (props) => {
	const [notification, setNotification] = useState<App2Notification | undefined>();

	const notify: App2NotifyProps = useCallback(
		(
			body,
			title = "",
			severity = App2NotificationSeverity.success,
			lifeTime = 5000,
			multiple = true
		) =>
			setNotification({
				title: title ?? "",
				body: body ?? "",
				severity,
				lifeTime: lifeTime ?? 5000,
				timeAdded: new Date(),
				multiple,
			}),
		[]
	);

	return (
		<App2NotificationContext.Provider value={{ notification, notify }}>
			{props.children}
		</App2NotificationContext.Provider>
	);
};
